<template>
  <interview-layout :touch-and-validate="touchAndValidate">
    <div>
      <h1 class="px-4 pb-3 fade-in-up-enter-active animation-slowest max-100 text-primary">VHF subsidiepercentage</h1>
      <h5 class="px-4 pb-3 fade-in-up-enter-active animation-slowest delay-200 max-100">Veendam</h5>
      <div class="d-flex flex-column flex-lg-row justify-content-center">
        <base-card inner-class="container-narrow w-100" color="alternate" :outer-padding="true">
          <base-form @submit="onSubmit">
            <base-card class="mb-3" inner-class="small" color="secondary">
              <h6 class="mb-2">VHF subsidie in Veendam</h6>
              <checkmark-item v-if="needsCivilStatus" class="mb-2" icon="info-circle-fill" icon-color="info">
                Om de VHF-schijven te berekenen, hebben we eerst nog nodig of u alleenstaand of gehuwd/samenwonend bent.
              </checkmark-item>
              <template v-else>
                <checkmark-item class="mb-2" icon="check-circle-fill" icon-color="success">
                  <strong>100%</strong> subsidie tot en met <strong>{{ formatted140 }}</strong>.
                </checkmark-item>
                <checkmark-item class="mb-2" icon="check-circle-fill" icon-color="success">
                  <strong>50%</strong> subsidie tussen <strong>{{ formatted140 }}</strong> en <strong>{{ formatted160 }}</strong>.
                </checkmark-item>
                <checkmark-item class="mb-2" icon="check-circle-fill" icon-color="success">
                  <strong>25%</strong> subsidie tussen <strong>{{ formatted160 }}</strong> en <strong>{{ formatted180 }}</strong>.
                </checkmark-item>
                <checkmark-item class="mb-0" icon="x-circle-fill" icon-color="danger">
                  Bij een inkomen boven <strong>{{ formatted180 }}</strong> komt u niet in aanmerking voor VHF.
                </checkmark-item>
              </template>
            </base-card>

            <div class="mt-3">
              <checkmark-item v-if="hasCivilStatus" class="mb-2" icon="check-circle-fill" icon-color="success">
                U heeft gekozen voor <strong>{{ civilStatusLabel }}</strong>.
              </checkmark-item>
              <checkmark-item v-else class="mb-2" icon="info-circle-fill" icon-color="info">
                Kies hier of u alleenstaand of gehuwd/samenwonend bent. Daarna tonen we de juiste inkomensschijven.
              </checkmark-item>
              <base-input
                type="radiobuttons"
                :options="civilStatusOptions"
                name="VhfCivilStatus"
                v-model:value="lead.financing.income.CivilStatus"
              >
                Bent u alleenstaand of gehuwd/samenwonend?
              </base-input>
            </div>

            <base-input
              v-if="!needsCivilStatus"
              class="mt-3"
              type="radiobuttons"
              :options="incomeBracketOptions"
              name="VhfIncomeSelection"
              v-model:value="vhfIncomeSelection"
            >
              In welke inkomenscategorie valt uw huishouden?
            </base-input>
          </base-form>
        </base-card>
      </div>

      <button-row class="mt-2 flex-row-reverse">
        <base-button class="ms-auto" icon="bi-arrow-right" @click="onSubmit" :disabled="!isComplete">Volgende</base-button>
        <base-button variant="minimal-secondary" icon="bi-arrow-left" icon-position="start" @click="onPrev">Terug</base-button>
      </button-row>
    </div>
  </interview-layout>
</template>

<script>
import InterviewLayout from "@/views/layouts/InterviewLayout.vue";
import BaseCard from "@/components/BaseCard.vue";
import BaseForm from "@/components/BaseForm.vue";
import BaseInput from "@/components/BaseInput.vue";
import BaseButton from "@/components/BaseButton.vue";
import ButtonRow from "@/components/ButtonRow.vue";
import CheckmarkItem from "@/components/CheckmarkItem.vue";
import { useAllStores } from "@/helpers/UseAllStores";
import { CommonComposable } from "@/helpers/CommonComposable";
import { FlowService } from "@/services/FlowService";
import { civilStatusOptions } from "@/services/SocialMinimumService";

export default {
  components: {
    InterviewLayout,
    BaseCard,
    BaseForm,
    BaseInput,
    BaseButton,
    ButtonRow,
    CheckmarkItem,
  },
  setup(props, context) {
    return { ...CommonComposable(props, context), ...useAllStores() };
  },
  data() {
    return {
      civilStatusOptions,
    };
  },
  computed: {
    needsCivilStatus() {
      const value = this.lead.financing?.income?.CivilStatus;
      return value === null || value === undefined || value === "";
    },
    hasCivilStatus() {
      return !this.needsCivilStatus;
    },
    amount140() {
      return this.hasCivilStatus ? (Number(this.lead.m29_SM140_amount) || 0) : 0;
    },
    amount160() {
      return Math.round((this.amount140 / 140) * 160);
    },
    amount180() {
      return Math.round((this.amount140 / 140) * 180);
    },
    formatted140() {
      return this.formatEuro(this.amount140);
    },
    formatted160() {
      return this.formatEuro(this.amount160);
    },
    formatted180() {
      return this.formatEuro(this.amount180);
    },
    civilStatusLabel() {
      const selected = this.civilStatusOptions.find((option) => option.value === this.lead.financing?.income?.CivilStatus);
      return selected ? selected.label : null;
    },
    incomeBracketOptions() {
      return [
        { label: `140% tot 160% (${this.formatted140} tot ${this.formatted160})`, value: "140to160" },
        { label: `160% tot 180% (${this.formatted160} tot ${this.formatted180})`, value: "160to180" },
        { label: `Boven 180% (boven ${this.formatted180})`, value: "above180" },
      ];
    },
    vhfIncomeSelection: {
      get() {
        const income = this.lead.financing?.income;
        if (!income) {
          return null;
        }

        if (income.SM_140pct === false && income.SM_160pct === true && income.SM_180pct === false) {
          return "140to160";
        }

        if (income.SM_140pct === false && income.SM_160pct === false && income.SM_180pct === true) {
          return "160to180";
        }

        if (income.SM_140pct === false && income.SM_160pct === false && income.SM_180pct === false) {
          return "above180";
        }

        return null;
      },
      set(value) {
        this.applyIncomeSelection(value);
      },
    },
    hasSelection() {
      return this.vhfIncomeSelection !== null;
    },
    isComplete() {
      return this.hasCivilStatus && this.hasSelection;
    },
  },
  methods: {
    applyIncomeSelection(selection) {
      const income = this.lead.financing?.income;
      if (!income) {
        return;
      }

      if (selection === "140to160") {
        income.SM_140pct = false;
        income.SM_160pct = true;
        income.SM_180pct = false;
        return;
      }

      if (selection === "160to180") {
        income.SM_140pct = false;
        income.SM_160pct = false;
        income.SM_180pct = true;
        return;
      }

      if (selection === "above180") {
        income.SM_140pct = false;
        income.SM_160pct = false;
        income.SM_180pct = false;
        return;
      }

      income.SM_140pct = null;
      income.SM_160pct = null;
      income.SM_180pct = null;
    },
    formatEuro(value) {
      return new Intl.NumberFormat("nl-NL", {
        style: "currency",
        currency: "EUR",
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(value || 0);
    },
    touchAndValidate() {
      return this.isComplete;
    },
    onSubmit() {
      if (this.touchAndValidate()) {
        FlowService.pushNext(this);
      }
    },
    onPrev() {
      FlowService.pushPrev(this);
    },
  },
  mounted() {
  },
};
</script>

<style scoped lang="scss">
</style>
